/**
 * Fase 27 — Hook de polling com visibility guard.
 *
 * Diferenças vs setInterval cru:
 *   - Pausa polling quando a aba está em background (visibilitychange)
 *   - Cleanup garantido (clearInterval + remoção de listener)
 *   - Roda 1x imediatamente ao montar
 *   - Reentrant guard (se a chamada anterior demora, não dispara nova)
 *   - v7.47: detecta DatabaseClosedError e tenta reabrir o Dexie em vez
 *     de silenciar erros para sempre (que causava "loading infinito"
 *     após race com versionchange/resetDatabase).
 *
 * Uso:
 *   useVisiblePolling(() => loadData(), 5000);
 *
 * v7.68 — options.ignoreVisibility: quando true, o polling continua rodando
 * mesmo com a aba em background. Usado pela Cozinha (KDS): o Firebase já
 * sincroniza os dados em segundo plano independente de visibilidade — o que
 * faltava era essa tela também atualizar a UI enquanto não está em foco, pra
 * não perder pedidos novos que chegaram com a tela minimizada/trocada.
 */
import { useEffect, useRef } from 'react';
import db, { isDbShuttingDown } from '@/db';

// Throttle de tentativas de reabrir o DB — evita 100 tentativas/seg em
// cenário patológico (DB destruído sem reset, browser bug, etc).
let _lastReopenAttempt = 0;
let _reopenInflight = null;
const REOPEN_MIN_INTERVAL_MS = 1500;

export async function tryReopenDb() {
  // Não reabre durante shutdown intencional (resetDatabase/versionchange).
  if (isDbShuttingDown()) return false;
  if (_reopenInflight) return _reopenInflight;
  const now = Date.now();
  if (now - _lastReopenAttempt < REOPEN_MIN_INTERVAL_MS) return false;
  _lastReopenAttempt = now;
  _reopenInflight = (async () => {
    try {
      console.warn('[POLL] DatabaseClosedError detectado — tentando reabrir Dexie.');
      // `db.open()` é idempotente: se já estiver aberto retorna o mesmo handle.
      await db.open();
      console.log('[POLL] Dexie reaberto com sucesso.');
      return true;
    } catch (err) {
      console.error('[POLL] Falha ao reabrir Dexie:', err?.name || err?.message || err);
      return false;
    } finally {
      _reopenInflight = null;
    }
  })();
  return _reopenInflight;
}

export function useVisiblePolling(fn, intervalMs, options = {}) {
  const { ignoreVisibility = false } = options;
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let intervalId = null;
    let inFlight = false;

    const tick = async () => {
      if (inFlight) return;
      if (!ignoreVisibility && typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      inFlight = true;
      try {
        await fnRef.current();
      } catch (err) {
        // v7.47 — Polling não pode ficar silenciosamente quebrado para sempre.
        // Se o DB foi fechado (race condition após versionchange ou reset
        // mal-finalizado em outro lugar do código), reabrimos uma vez e
        // tentamos de novo no próximo tick. Outros erros continuam silenciosos
        // (a UI não deve crashar por causa de polling).
        const name = err?.name || '';
        const msg = err?.message || '';
        if (name === 'DatabaseClosedError' || /database.*closed/i.test(msg)) {
          const reopened = await tryReopenDb();
          if (reopened) {
            // Tenta uma vez de novo após reabrir, no próximo tick natural —
            // não chamamos fnRef.current() aqui pra evitar storm em cascata.
          }
        }
        // Demais erros: silencioso (mantém comportamento legado).
      } finally {
        inFlight = false;
      }
    };

    // Roda 1x imediatamente
    tick();
    intervalId = setInterval(tick, intervalMs);

    // Re-fire ao voltar pra aba (UX: dado atualiza imediato)
    const onVis = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [intervalMs, ignoreVisibility]);
}
